import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Token Management ───────────────────────────────────────────────────────
let accessToken = localStorage.getItem('phoenix_access_token');
let refreshToken = localStorage.getItem('phoenix_refresh_token');

export const setTokens = (access, refresh) => {
  accessToken = access;
  refreshToken = refresh;
  if (access) localStorage.setItem('phoenix_access_token', access);
  else localStorage.removeItem('phoenix_access_token');
  if (refresh) localStorage.setItem('phoenix_refresh_token', refresh);
  else localStorage.removeItem('phoenix_refresh_token');
};

export const clearTokens = () => {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('phoenix_access_token');
  localStorage.removeItem('phoenix_refresh_token');
  localStorage.removeItem('phoenix_user');
  localStorage.removeItem('phoenix_modules');
};

export const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem('phoenix_user')); }
  catch { return null; }
};

export const storeUser = (user, modules) => {
  localStorage.setItem('phoenix_user', JSON.stringify(user));
  localStorage.setItem('phoenix_modules', JSON.stringify(modules));
};

export const getStoredModules = () => {
  try { return JSON.parse(localStorage.getItem('phoenix_modules')) || []; }
  catch { return []; }
};

// ─── Request Interceptor — attach JWT ───────────────────────────────────────
api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// ─── Response Interceptor — handle token refresh ────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // License expired — don't retry, let the UI handle it
    if (error.response?.status === 402) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && error.response?.data?.code === 'TOKEN_EXPIRED' && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
        setTokens(data.accessToken, data.refreshToken);
        processQueue(null, data.accessToken);
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ═══════════════════════════════════════════════════════════════════════════
// API Methods — organized by domain
// ═══════════════════════════════════════════════════════════════════════════

export const auth = {
  login: (email, password, orgSlug, orgId, mfaCode) =>
    api.post('/auth/login', { email, password, orgSlug, orgId, mfaCode }),
  loginMfa: (mfaChallenge, mfaCode) =>
    api.post('/auth/login/mfa', { mfaChallenge, mfaCode }),
  refresh: () =>
    api.post('/auth/refresh', { refreshToken }),
  logout: () =>
    api.post('/auth/logout', { refreshToken }),
  me: () =>
    api.get('/auth/me'),
  registerOrg: (data) =>
    api.post('/auth/register-org', data),
  forgotPassword: (email) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, newPassword }),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
};

export const mfa = {
  status: () => api.get('/auth/mfa/status'),
  setup: () => api.post('/auth/mfa/setup'),
  enable: (code) => api.post('/auth/mfa/enable', { code }),
  disable: (code) => api.post('/auth/mfa/disable', { code }),
  regenerateBackupCodes: (code) => api.post('/auth/mfa/regenerate-backup-codes', { code }),
};

export const auditLog = {
  list: (params) => api.get('/audit-log', { params }),
  actors: () => api.get('/audit-log/actors'),
};

export const displayControl = {
  // Staff endpoints — list kiosks for the current org and read/write
  // their workout-display state. Used by the trainer's phone/iPad remote.
  kiosks: () => api.get('/display-control/kiosks'),
  getState: (kioskId) => api.get(`/display-control/${kioskId}/state`),
  putState: (kioskId, patch) => api.put(`/display-control/${kioskId}/state`, patch),
};

export const students = {
  list: (params) =>
    api.get('/students', { params }),
  get: (id) =>
    api.get(`/students/${id}`),
  create: (data) =>
    api.post('/students', data),
  update: (id, data) =>
    api.put(`/students/${id}`, data),
  delete: (id) =>
    api.delete(`/students/${id}`),
};

export const equipment = {
  list: (params) =>
    api.get('/equipment', { params }),
  create: (data) =>
    api.post('/equipment', data),
  update: (id, data) =>
    api.put(`/equipment/${id}`, data),
  delete: (id) =>
    api.delete(`/equipment/${id}`),
};

export const inventory = {
  list: (params) =>
    api.get('/inventory', { params }),
  get: (id) =>
    api.get(`/inventory/${id}`),
  create: (data) =>
    api.post('/inventory', data),
  update: (id, data) =>
    api.put(`/inventory/${id}`, data),
  delete: (id) =>
    api.delete(`/inventory/${id}`),
  addVariant: (id, data) =>
    api.post(`/inventory/${id}/variants`, data),
  updateVariant: (variantId, data) =>
    api.put(`/inventory/variants/${variantId}`, data),
  deleteVariant: (variantId) =>
    api.delete(`/inventory/variants/${variantId}`),
};

export const classes = {
  list: (params) =>
    api.get('/classes', { params }),
  create: (data) =>
    api.post('/classes', data),
  update: (id, data) =>
    api.put(`/classes/${id}`, data),
  delete: (id) =>
    api.delete(`/classes/${id}`),
  // Sessions
  sessions: (classId, params) =>
    api.get(`/classes/${classId}/sessions`, { params }),
  createSession: (classId, data) =>
    api.post(`/classes/${classId}/sessions`, data),
  generateSessions: (classId, data) =>
    api.post(`/classes/${classId}/sessions/generate`, data),
  updateSession: (sessionId, data) =>
    api.put(`/classes/sessions/${sessionId}`, data),
  deleteSession: (sessionId) =>
    api.delete(`/classes/sessions/${sessionId}`),
  // Enrollments
  enrollments: (classId) =>
    api.get(`/classes/${classId}/enrollments`),
  enroll: (classId, data) =>
    api.post(`/classes/${classId}/enroll`, data),
  updateEnrollment: (enrollmentId, data) =>
    api.put(`/classes/enrollments/${enrollmentId}`, data),
  // Student self-booking
  book: (classId) =>
    api.post(`/classes/${classId}/book`),
  dropBooking: (classId) =>
    api.delete(`/classes/${classId}/book`),
  // Session attendance
  sessionAttendance: (sessionId) =>
    api.get(`/classes/sessions/${sessionId}/attendance`),
  markAttendance: (sessionId, data) =>
    api.post(`/classes/sessions/${sessionId}/attendance`, data),
  // Reporting
  attendanceReport: (classId, params) =>
    api.get(`/classes/${classId}/attendance-report`, { params }),
  studentAttendance: (studentId, params) =>
    api.get(`/classes/student/${studentId}/attendance`, { params }),
  schedule: (params) =>
    api.get('/classes/schedule', { params }),
  studentSchedule: (studentId) =>
    api.get(`/classes/student/${studentId}/schedule`),
  // Session plan & history
  sessionPlan: (sessionId) =>
    api.get(`/classes/sessions/${sessionId}/plan`),
  classPlanHistory: (classId, params) =>
    api.get(`/classes/${classId}/plan-history`, { params }),
};

export const contracts = {
  list: () =>
    api.get('/contracts'),
  get: (id) =>
    api.get(`/cf/contracts/${id}`),
  create: (data) =>
    api.post('/contracts', data),
  update: (id, data) =>
    api.put(`/contracts/${id}`, data),
  delete: (id) =>
    api.delete(`/contracts/${id}`),
  preview: (id, studentId) =>
    api.get(`/cf/contracts/${id}/preview/${studentId}`),
  sign: (id, data) =>
    api.post(`/cf/contracts/${id}/sign`, data),
  myContracts: () =>
    api.get('/cf/contracts/student/mine'),
  signatures: (id) =>
    api.get(`/cf/contracts/${id}/signatures`),
  seedTemplates: () =>
    api.post('/cf/contracts/seed-templates'),
  assign: (id, data) =>
    api.post(`/cf/contracts/${id}/assign`, data),
  assignments: (id) =>
    api.get(`/cf/contracts/${id}/assignments`),
  removeAssignment: (id, assignmentId) =>
    api.delete(`/cf/contracts/${id}/assignments/${assignmentId}`),
  duplicate: (id, data) =>
    api.post(`/cf/contracts/${id}/duplicate`, data),
  outstanding: () =>
    api.get('/cf/outstanding'),
};

export const forms = {
  list: () =>
    api.get('/forms'),
  get: (id) =>
    api.get(`/cf/forms/${id}`),
  create: (data) =>
    api.post('/forms', data),
  update: (id, data) =>
    api.put(`/forms/${id}`, data),
  delete: (id) =>
    api.delete(`/forms/${id}`),
  submit: (id, data) =>
    api.post(`/cf/forms/${id}/submit`, data),
  myForms: () =>
    api.get('/cf/forms/student/mine'),
  submissions: (id, params) =>
    api.get(`/cf/forms/${id}/submissions`, { params }),
  reviewSubmission: (id, data) =>
    api.put(`/cf/forms/submissions/${id}/review`, data),
  seedTemplates: () =>
    api.post('/cf/forms/seed-templates'),
  assign: (id, data) =>
    api.post(`/cf/forms/${id}/assign`, data),
  formAssignments: (id) =>
    api.get(`/cf/forms/${id}/assignments`),
  removeFormAssignment: (id, assignmentId) =>
    api.delete(`/cf/forms/${id}/assignments/${assignmentId}`),
};

export const messages = {
  conversations: () =>
    api.get('/messages/conversations'),
  getMessages: (convoId) =>
    api.get(`/messages/conversations/${convoId}/messages`),
  send: (convoId, body) =>
    api.post(`/messages/conversations/${convoId}/messages`, { body }),
  createConversation: (data) =>
    api.post('/messages/conversations', data),
};

export const payments = {
  list: (params) =>
    api.get('/payments', { params }),
  create: (data) =>
    api.post('/payments', data),
};

export const martialArts = {
  ranks: (params) =>
    api.get('/martial-arts/ranks', { params }),
  awardRank: (data) =>
    api.post('/martial-arts/ranks', data),
  gradings: (params) =>
    api.get('/martial-arts/gradings', { params }),
};

export const disciplines = {
  list: () =>
    api.get('/disciplines'),
  get: (id) =>
    api.get(`/disciplines/${id}`),
  create: (data) =>
    api.post('/disciplines', data),
  update: (id, data) =>
    api.put(`/disciplines/${id}`, data),
  delete: (id) =>
    api.delete(`/disciplines/${id}`),
  ranks: (disciplineId) =>
    api.get(`/disciplines/${disciplineId}/ranks`),
  createRank: (disciplineId, data) =>
    api.post(`/disciplines/${disciplineId}/ranks`, data),
  updateRank: (rankId, data) =>
    api.put(`/disciplines/ranks/${rankId}`, data),
  bulkUpdateRanks: (disciplineId, data) =>
    api.put(`/disciplines/${disciplineId}/ranks/bulk`, data),
  students: (disciplineId) =>
    api.get(`/disciplines/${disciplineId}/students`),
  enrollStudent: (disciplineId, data) =>
    api.post(`/disciplines/${disciplineId}/enroll`, data),
  unenrollStudent: (disciplineId, studentId) =>
    api.delete(`/disciplines/${disciplineId}/unenroll/${studentId}`),
};

export const fitness = {
  measurements: (studentId) =>
    api.get(`/fitness/measurements/${studentId}`),
  recordMeasurement: (data) =>
    api.post('/fitness/measurements', data),
  accessLog: () =>
    api.get('/fitness/access-log'),
};

// Admin-side mobile activity aggregates (mirror the mobile modules)
export const memberActivity = {
  workouts: (studentId, days = 30) => api.get(`/live-class/student/${studentId}/workouts`, { params: { days } }),
  foodLog: (studentId, date) => api.get('/food-log', { params: { studentId, date } }),
  nutritionTargets: (studentId) => api.get(`/food-log/targets/${studentId}`),
  saveTargets: (studentId, data) => api.put(`/food-log/targets/${studentId}`, data),
};

export const adminCommunity = {
  leaderboard: (type, period, limit) => api.get('/community/leaderboards', { params: { type, period, limit } }),
  feed: (limit = 50) => api.get('/community/feed', { params: { limit } }),
};

export const exportsApi = {
  orgDataUrl: (token) => `${API_URL}/exports/org-data?token=${encodeURIComponent(token || '')}`,
  tableCsvUrl: (name, token) => `${API_URL}/exports/table/${encodeURIComponent(name)}.csv?token=${encodeURIComponent(token || '')}`,
  tables: () => api.get('/exports/tables'),
};

export const ml = {
  settings: () => api.get('/ml/settings'),
  updateSettings: (data) => api.put('/ml/settings', data),
  summary: () => api.get('/ml/summary'),
  scores: (scope, params) => api.get(`/ml/scores/${scope}`, { params }),
  score: (scope, subjectId) => api.get(`/ml/scores/${scope}/${subjectId}`),
  rescoreAll: (scope) => api.post(`/ml/rescore/${scope}`),
  rescoreOne: (scope, subjectId) => api.post(`/ml/rescore/${scope}/${subjectId}`),
  train: (scope) => api.post(`/ml/train/${scope}`),
  bootstrap: (scope) => api.post(`/ml/bootstrap/${scope}`),
  models: (scope) => api.get(`/ml/models/${scope}`),
  interventions: (scope) => api.get(`/ml/interventions/${scope}`),
  updateInterventions: (scope, interventions) => api.put(`/ml/interventions/${scope}`, { interventions }),
};

export const whiteLabel = {
  config: () => api.get('/white-label/config'),
  updateConfig: (data) => api.put('/white-label/config', data),
  appConfig: () => api.get('/white-label/config/app-config.json'),
  easConfig: (profile) => api.get('/white-label/config/eas.json', { params: { profile } }),
  builds: () => api.get('/white-label/builds'),
  requestBuild: (data) => api.post('/white-label/builds', data),
  cancelBuild: (id) => api.post(`/white-label/builds/${id}/cancel`),
};

export const accountingSync = {
  syncSettings: (data) => api.put('/accounting/sync-settings', data),
  syncLog: (params) => api.get('/accounting/sync-log', { params }),
  syncPayment: (id) => api.post(`/accounting/sync/payment/${id}`),
  syncOrder: (id) => api.post(`/accounting/sync/order/${id}`),
  backfill: (days) => api.post('/accounting/sync/backfill', { days }),
};

export const payroll = {
  payRuns: () => api.get('/payroll/pay-runs'),
  createPayRun: (data) => api.post('/payroll/pay-runs', data),
  payRun: (id) => api.get(`/payroll/pay-runs/${id}`),
  updateSlip: (id, data) => api.put(`/payroll/pay-slips/${id}`, data),
  finalise: (id) => api.post(`/payroll/pay-runs/${id}/finalise`),
  stpPayloadUrl: (id, token) => `${API_URL}/payroll/pay-runs/${id}/stp/payload?token=${encodeURIComponent(token || '')}`,
  lodgeStp: (id, data) => api.post(`/payroll/pay-runs/${id}/stp/lodge`, data),
  submitStp: (id) => api.post(`/payroll/pay-runs/${id}/stp/submit`),
  regenerateStp: (id) => api.post(`/payroll/pay-runs/${id}/stp/regenerate`),
  stpSettings: () => api.get('/payroll/stp-settings'),
  updateStpSettings: (data) => api.put('/payroll/stp-settings', data),
  employees: () => api.get('/payroll/employees'),
  updateEmployee: (userId, data) => api.put(`/payroll/employees/${userId}`, data),
};

export const community = {
  leaderboard: (type = 'attendance', period = 'week', limit = 10) =>
    api.get('/community/leaderboards', { params: { type, period, limit } }),
  feed: (limit = 30) => api.get('/community/feed', { params: { limit } }),
};

export const videos = {
  list: (params) => api.get('/videos', { params }),
  get: (id) => api.get(`/videos/${id}`),
  create: (data) => api.post('/videos', data),
  update: (id, data) => api.put(`/videos/${id}`, data),
  delete: (id) => api.delete(`/videos/${id}`),
  view: (id) => api.post(`/videos/${id}/view`),
};

export const programs = {
  list: (params) =>
    api.get('/programs', { params }),
  get: (id) =>
    api.get(`/programs/${id}`),
  create: (data) =>
    api.post('/programs', data),
  update: (id, data) =>
    api.put(`/programs/${id}`, data),
  delete: (id) =>
    api.delete(`/programs/${id}`),
  addPlan: (id, data) =>
    api.post(`/programs/${id}/plans`, data),
  updatePlanLink: (id, linkId, data) =>
    api.put(`/programs/${id}/plans/${linkId}`, data),
  removePlan: (id, linkId) =>
    api.delete(`/programs/${id}/plans/${linkId}`),
  assign: (id, data) =>
    api.post(`/programs/${id}/assign`, data),
  unassign: (id, studentId) =>
    api.delete(`/programs/${id}/assign/${studentId}`),
  myPrograms: () =>
    api.get('/programs/my-programs'),
  studentPrograms: (studentId) =>
    api.get(`/programs/student/${studentId}`),
};

export const practitioner = {
  generateCode: (data) =>
    api.post('/practitioner/generate-code', data),
  myCodes: () =>
    api.get('/practitioner/my-codes'),
  revokeCode: (id) =>
    api.delete(`/practitioner/revoke/${id}`),
};

export const exercises = {
  list: (params) =>
    api.get('/exercises', { params }),
  get: (id) =>
    api.get(`/exercises/${id}`),
  create: (data) =>
    api.post('/exercises', data),
  update: (id, data) =>
    api.put(`/exercises/${id}`, data),
  delete: (id) =>
    api.delete(`/exercises/${id}`),
};

export const exercisePlans = {
  list: (params) =>
    api.get('/exercise-plans', { params }),
  get: (id) =>
    api.get(`/exercise-plans/${id}`),
  create: (data) =>
    api.post('/exercise-plans', data),
  update: (id, data) =>
    api.put(`/exercise-plans/${id}`, data),
  delete: (id) =>
    api.delete(`/exercise-plans/${id}`),
  duplicate: (id, data) =>
    api.post(`/exercise-plans/${id}/duplicate`, data),
  addSection: (planId, data) =>
    api.post(`/exercise-plans/${planId}/sections`, data),
  updateSection: (sectionId, data) =>
    api.put(`/exercise-plans/sections/${sectionId}`, data),
  deleteSection: (sectionId) =>
    api.delete(`/exercise-plans/sections/${sectionId}`),
  addGroup: (sectionId, data) =>
    api.post(`/exercise-plans/sections/${sectionId}/groups`, data),
  updateGroup: (groupId, data) =>
    api.put(`/exercise-plans/groups/${groupId}`, data),
  deleteGroup: (groupId) =>
    api.delete(`/exercise-plans/groups/${groupId}`),
  addGroupItem: (groupId, data) =>
    api.post(`/exercise-plans/groups/${groupId}/items`, data),
  addItem: (sectionId, data) =>
    api.post(`/exercise-plans/sections/${sectionId}/items`, data),
  updateItem: (itemId, data) =>
    api.put(`/exercise-plans/items/${itemId}`, data),
  deleteItem: (itemId) =>
    api.delete(`/exercise-plans/items/${itemId}`),
  assign: (planId, data) =>
    api.post(`/exercise-plans/${planId}/assign`, data),
  updateAssignment: (assignmentId, data) =>
    api.put(`/exercise-plans/assignments/${assignmentId}`, data),
  studentPlans: (studentId) =>
    api.get(`/exercise-plans/student/${studentId}`),
  log: (data) =>
    api.post('/exercise-plans/log', data),
  getLog: (studentPlanId) =>
    api.get(`/exercise-plans/log/${studentPlanId}`),
};

export const beltProgression = {
  requirements: (params) =>
    api.get('/belt-progression/requirements', { params }),
  upsertRequirement: (rankId, data) =>
    api.put(`/belt-progression/requirements/${rankId}`, data),
  deleteRequirement: (rankId) =>
    api.delete(`/belt-progression/requirements/${rankId}`),
  syllabus: (params) =>
    api.get('/belt-progression/syllabus', { params }),
  createSyllabusItem: (data) =>
    api.post('/belt-progression/syllabus', data),
  updateSyllabusItem: (id, data) =>
    api.put(`/belt-progression/syllabus/${id}`, data),
  deleteSyllabusItem: (id) =>
    api.delete(`/belt-progression/syllabus/${id}`),
  studentProgress: (studentId, params) =>
    api.get(`/belt-progression/student/${studentId}/progress`, { params }),
  markProgress: (studentId, data) =>
    api.post(`/belt-progression/student/${studentId}/progress`, data),
  removeProgress: (studentId, itemId) =>
    api.delete(`/belt-progression/student/${studentId}/progress/${itemId}`),
  eligibility: (studentId, params) =>
    api.get(`/belt-progression/eligibility/${studentId}`, { params }),
  readiness: (params) =>
    api.get('/belt-progression/readiness', { params }),
  createGrading: (data) =>
    api.post('/belt-progression/gradings', data),
  updateGrading: (id, data) =>
    api.put(`/belt-progression/gradings/${id}`, data),
  addCandidate: (gradingId, data) =>
    api.post(`/belt-progression/gradings/${gradingId}/candidates`, data),
  updateCandidate: (candidateId, data) =>
    api.put(`/belt-progression/gradings/candidates/${candidateId}`, data),
  completeGrading: (gradingId) =>
    api.post(`/belt-progression/gradings/${gradingId}/complete`),
  listGradings: (params) =>
    api.get('/belt-progression/gradings', { params }),
  gradingCandidates: (gradingId) =>
    api.get(`/belt-progression/gradings/${gradingId}/candidates`),
  inviteCandidates: (gradingId, data) =>
    api.post(`/belt-progression/gradings/${gradingId}/invite`, data),
  respondToInvitation: (candidateId, data) =>
    api.put(`/belt-progression/gradings/candidates/${candidateId}/respond`, data),
  payGradingFee: (candidateId, data) =>
    api.post(`/belt-progression/gradings/candidates/${candidateId}/pay`, data),
  myGradings: () =>
    api.get('/belt-progression/student/gradings'),
};

export const subscriptionPlans = {
  list: (params) =>
    api.get('/subscription-plans', { params }),
  get: (id) =>
    api.get(`/subscription-plans/${id}`),
  create: (data) =>
    api.post('/subscription-plans', data),
  update: (id, data) =>
    api.put(`/subscription-plans/${id}`, data),
  delete: (id) =>
    api.delete(`/subscription-plans/${id}`),
  addDiscount: (planId, data) =>
    api.post(`/subscription-plans/${planId}/discounts`, data),
  updateDiscount: (planId, discountId, data) =>
    api.put(`/subscription-plans/${planId}/discounts/${discountId}`, data),
  deleteDiscount: (planId, discountId) =>
    api.delete(`/subscription-plans/${planId}/discounts/${discountId}`),
  calculatePrice: (planId, data) =>
    api.post(`/subscription-plans/${planId}/calculate-price`, data),
  planClasses: (planId) =>
    api.get(`/subscription-plans/${planId}/classes`),
  updatePlanClasses: (planId, data) =>
    api.put(`/subscription-plans/${planId}/classes`, data),
  removePlanClass: (planId, classId) =>
    api.delete(`/subscription-plans/${planId}/classes/${classId}`),
};

export const organization = {
  get: () =>
    api.get('/organization'),
  update: (data) =>
    api.put('/organization', data),
  staff: () =>
    api.get('/organization/staff'),
  createStaff: (data) =>
    api.post('/organization/staff', data),
  updateStaff: (id, data) =>
    api.put(`/organization/staff/${id}`, data),
  deleteStaff: (id) =>
    api.delete(`/organization/staff/${id}`),
  staffPermissions: (staffId) =>
    api.get(`/organization/staff/${staffId}/permissions`),
  updateStaffPermissions: (staffId, permissions) =>
    api.put(`/organization/staff/${staffId}/permissions`, { permissions }),
  licenseStatus: () =>
    api.get('/organization/license-status'),
  onboarding: () =>
    api.get('/organization/onboarding'),
  markOnboardingStep: (step) =>
    api.put(`/organization/onboarding/${step}`),
  skipOnboarding: () =>
    api.post('/organization/onboarding/skip'),
};

// License management (super_admin only — used by license portal)
export const licenses = {
  organizations: (params) =>
    api.get('/licenses/organizations', { params }),
  getOrg: (id) =>
    api.get(`/licenses/organizations/${id}`),
  issue: (data) =>
    api.post('/licenses/issue', data),
  update: (id, data) =>
    api.put(`/licenses/${id}`, data),
  revoke: (id) =>
    api.delete(`/licenses/${id}`),
  stats: () =>
    api.get('/licenses/stats'),
};

export const shop = {
  products: (params) =>
    api.get('/shop/products', { params }),
  getProduct: (id) =>
    api.get(`/shop/products/${id}`),
  createOrder: (data) =>
    api.post('/shop/orders', data),
  orders: (params) =>
    api.get('/shop/orders', { params }),
  getOrder: (id) =>
    api.get(`/shop/orders/${id}`),
  receiptUrl: (id) =>
    `${API_URL}/shop/orders/${id}/receipt`,
  createRefund: (data) =>
    api.post('/shop/refunds', data),
  refunds: (params) =>
    api.get('/shop/refunds', { params }),
  getRefund: (id) =>
    api.get(`/shop/refunds/${id}`),
  accountBalance: () =>
    api.get('/shop/account-balance'),
  topUp: (data) =>
    api.post('/shop/account-topup', data),
};

export const accounting = {
  getConfig: () => api.get('/accounting/config'),
  updateConfig: (data) => api.put('/accounting/config', data),
  getOAuthUrl: (provider) => api.get(`/accounting/oauth/${provider}`),
  disconnect: () => api.delete('/accounting/disconnect'),
  getAccounts: () => api.get('/accounting/accounts'),
  getMappings: () => api.get('/accounting/mappings'),
  updateMappings: (data) => api.put('/accounting/mappings', data),
  generateJournals: (data) => api.post('/accounting/generate', data),
  listJournals: (params) => api.get('/accounting/journals', { params }),
  getJournal: (id) => api.get(`/accounting/journals/${id}`),
  syncAll: () => api.post('/accounting/sync'),
  syncOne: (id) => api.post(`/accounting/sync/${id}`),
  deleteJournal: (id) => api.delete(`/accounting/journals/${id}`),
  exportCsvUrl: (params) => `${API_URL}/accounting/journals/export?${new URLSearchParams(params || {}).toString()}`,
  listDepreciation: () => api.get('/accounting/depreciation'),
  createDepreciation: (data) => api.post('/accounting/depreciation', data),
  updateDepreciation: (id, data) => api.put(`/accounting/depreciation/${id}`, data),
  deleteDepreciation: (id) => api.delete(`/accounting/depreciation/${id}`),
  generateDepreciation: (data) => api.post('/accounting/depreciation/generate', data),
  // Two-way sync (Xero + QBO)
  getSyncSettings: () => api.get('/accounting/config'),
  updateSyncSettings: (data) => api.put('/accounting/sync-settings', data),
  syncLog: (params) => api.get('/accounting/sync-log', { params }),
  syncPayment: (id) => api.post(`/accounting/sync/payment/${id}`),
  syncOrder: (id) => api.post(`/accounting/sync/order/${id}`),
  syncBackfill: (days) => api.post('/accounting/sync/backfill', { days }),
};

export const reports = {
  students: (params) => api.get('/reports/students', { params }),
  revenue: (params) => api.get('/reports/revenue', { params }),
  utilisation: (params) => api.get('/reports/utilisation', { params }),
  inventory: (params) => api.get('/reports/inventory', { params }),
  marketing: (params) => api.get('/reports/marketing', { params }),
  trials: (params) => api.get('/reports/trials', { params }),
  attendance: (params) => api.get('/reports/attendance', { params }),
  cohorts: (params) => api.get('/reports/cohorts', { params }),
  ltv: () => api.get('/reports/ltv'),
  churn: (params) => api.get('/reports/churn', { params }),
  monthly: (params) => api.get('/reports/monthly', { params }),
  monthlyCsv: (params) => `${API_URL}/reports/monthly/csv?${new URLSearchParams(params || {}).toString()}`,
  exportCsv: (type, params) => `${API_URL}/reports/export/${type}?${new URLSearchParams(params || {}).toString()}`,
  exportPdf: (type, params) => `${API_URL}/reports/pdf/${type}?${new URLSearchParams(params || {}).toString()}`,
};

export const adminReferrals = {
  list: () => api.get('/referrals'),
  credit: (id) => api.post(`/referrals/${id}/credit`),
  saveSettings: (data) => api.put('/referrals/settings', data),
};

export const adminInstallments = {
  forStudent: (studentId) => api.get(`/installment-plans/${studentId}`),
  create: (data) => api.post('/installment-plans', data),
  cancel: (planId) => api.patch(`/installment-plans/${planId}/cancel`),
  updateItem: (id, data) => api.patch(`/installment-plans/installments/${id}`, data),
};

export const apiKeys = {
  list: () => api.get('/admin/api-keys'),
  scopes: () => api.get('/admin/api-keys/scopes'),
  create: (data) => api.post('/admin/api-keys', data),
  revoke: (id) => api.post(`/admin/api-keys/${id}/revoke`),
};

export const orgGroups = {
  list: () => api.get('/org-groups'),
  create: (data) => api.post('/org-groups', data),
  update: (id, data) => api.put(`/org-groups/${id}`, data),
  members: (id) => api.get(`/org-groups/${id}/members`),
  addMember: (id, orgId, groupRole) => api.post(`/org-groups/${id}/members`, { orgId, groupRole }),
  removeMember: (id, orgId) => api.delete(`/org-groups/${id}/members/${orgId}`),
  summary: (id) => api.get(`/org-groups/${id}/summary`),
};

export const promoCodes = {
  list: () => api.get('/promo-codes'),
  create: (data) => api.post('/promo-codes', data),
  update: (id, data) => api.put(`/promo-codes/${id}`, data),
  delete: (id) => api.delete(`/promo-codes/${id}`),
  validate: (data) => api.post('/promo-codes/validate', data),
};

export const marketing = {
  campaigns: (params) => api.get('/marketing/campaigns', { params }),
  getCampaign: (id) => api.get(`/marketing/campaigns/${id}`),
  createCampaign: (data) => api.post('/marketing/campaigns', data),
  updateCampaign: (id, data) => api.put(`/marketing/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/marketing/campaigns/${id}`),
  sendCampaign: (id) => api.post(`/marketing/campaigns/${id}/send`),
  campaignStats: (id) => api.get(`/marketing/campaigns/${id}/stats`),
  templates: () => api.get('/marketing/templates'),
  createTemplate: (data) => api.post('/marketing/templates', data),
  updateTemplate: (id, data) => api.put(`/marketing/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/marketing/templates/${id}`),
  seedTemplates: () => api.post('/marketing/templates/seed'),
  previewTemplate: (id) => api.get(`/marketing/templates/${id}/preview`),
  events: () => api.get('/marketing/events'),
  getEvent: (id) => api.get(`/marketing/events/${id}`),
  createEvent: (data) => api.post('/marketing/events', data),
  updateEvent: (id, data) => api.put(`/marketing/events/${id}`, data),
  bookEvent: (id, data) => api.post(`/marketing/events/${id}/book`, data),
  updateBooking: (id, data) => api.put(`/marketing/events/bookings/${id}`, data),
  notificationLog: (params) => api.get('/marketing/log', { params }),
  quickSend: (data) => api.post('/marketing/send', data),
};

export const siteAdmin = {
  getSettings: () => api.get('/site-admin/settings'),
  updateSettings: (data) => api.put('/site-admin/settings', data),
  // Blog
  listBlog: () => api.get('/site-admin/blog'),
  createBlog: (data) => api.post('/site-admin/blog', data),
  updateBlog: (id, data) => api.put(`/site-admin/blog/${id}`, data),
  deleteBlog: (id) => api.delete(`/site-admin/blog/${id}`),
  // Trials
  listTrials: (params) => api.get('/site-admin/trials', { params }),
  updateTrial: (id, data) => api.put(`/site-admin/trials/${id}`, data),
  // Contacts
  listContacts: () => api.get('/site-admin/contacts'),
  updateContact: (id, data) => api.put(`/site-admin/contacts/${id}`, data),
  // Instructors
  listInstructors: () => api.get('/site-admin/instructors'),
  createInstructor: (data) => api.post('/site-admin/instructors', data),
  updateInstructor: (id, data) => api.put(`/site-admin/instructors/${id}`, data),
  deleteInstructor: (id) => api.delete(`/site-admin/instructors/${id}`),
};

export const stripe = {
  config: () =>
    api.get('/stripe/config'),
  checkoutShop: (data) =>
    api.post('/stripe/checkout/shop', data),
  checkoutGrading: (data) =>
    api.post('/stripe/checkout/grading', data),
  checkoutSubscription: (data) =>
    api.post('/stripe/checkout/subscription', data),
  customerPortal: (data) =>
    api.post('/stripe/customer-portal', data),
  setupCheckoutLink: (data) =>
    api.post('/stripe/setup-checkout-link', data),
  syncCardFromStripe: (data) =>
    api.post('/stripe/sync-card-from-stripe', data),
};

export const checkin = {
  // Kiosk management
  kiosks: () => api.get('/checkin/kiosks'),
  createKiosk: (data) => api.post('/checkin/kiosks', data),
  deleteKiosk: (id) => api.delete(`/checkin/kiosks/${id}`),
  // Self check-in (student)
  selfCheckin: (data) => api.post('/checkin/self-checkin', data),
  // Preferences
  getPreferences: () => api.get('/checkin/preferences'),
  updatePreferences: (data) => api.put('/checkin/preferences', data),
  // Attendance dashboard
  attendanceStats: (params) => api.get('/checkin/attendance/stats', { params }),
  attendanceByStudent: (params) => api.get('/checkin/attendance/by-student', { params }),
  attendanceByClass: (params) => api.get('/checkin/attendance/by-class', { params }),
  // Access tokens
  tokens: () => api.get('/checkin/tokens'),
  createToken: (data) => api.post('/checkin/tokens', data),
  updateToken: (id, data) => api.put(`/checkin/tokens/${id}`, data),
  deleteToken: (id) => api.delete(`/checkin/tokens/${id}`),
  // Body metrics
  studentMetrics: (studentId) => api.get(`/checkin/metrics/student/${studentId}`),
  addMeasurement: (data) => api.post('/checkin/metrics', data),
  addSelfMeasurement: (data) => api.post('/checkin/metrics/self', data),
  goals: (studentId) => api.get(`/checkin/metrics/goals/${studentId}`),
  createGoal: (data) => api.post('/checkin/metrics/goals', data),
  updateGoal: (id, data) => api.put(`/checkin/metrics/goals/${id}`, data),
  deleteGoal: (id) => api.delete(`/checkin/metrics/goals/${id}`),
};

export const smsCredits = {
  packs: () => api.get('/sms-credits/packs'),
  balance: () => api.get('/sms-credits/balance'),
  transactions: () => api.get('/sms-credits/transactions'),
  purchase: (packId) => api.post('/sms-credits/purchase', { packId }),
  grant: (data) => api.post('/sms-credits/grant', data),
  setBillingType: (billingType) => api.put('/sms-credits/billing-type', { billingType }),
};

export const twilioNumbers = {
  status: () => api.get('/twilio-numbers/status'),
  search: (params) => api.get('/twilio-numbers/search', { params }),
  purchase: (phoneNumber) => api.post('/twilio-numbers/purchase', { phoneNumber }),
  release: () => api.post('/twilio-numbers/release'),
  updateBilling: (data) => api.put('/twilio-numbers/billing', data),
};

export const uploads = {
  upload: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/uploads', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getUrl: (orgId, filename) => `${API_URL}/uploads/${orgId}/${filename}`,
};

export const timesheets = {
  // Clock in/out
  myStatus: () => api.get('/timesheets/my-status'),
  clockIn: () => api.post('/timesheets/clock-in'),
  clockOut: (data) => api.post('/timesheets/clock-out', data),
  myEntries: (params) => api.get('/timesheets/my-entries', { params }),

  // Admin
  list: (params) => api.get('/timesheets', { params }),
  create: (data) => api.post('/timesheets', data),
  update: (id, data) => api.put(`/timesheets/${id}`, data),
  remove: (id) => api.delete(`/timesheets/${id}`),
  approve: (entryIds, action) => api.post('/timesheets/approve', { entryIds, action }),
  markPaid: (entryIds) => api.post('/timesheets/mark-paid', { entryIds }),

  // Summary & export
  summary: (params) => api.get('/timesheets/summary', { params }),
  exportUrl: (startDate, endDate, format) =>
    `${API_URL}/timesheets/export?startDate=${startDate}&endDate=${endDate}&format=${format || 'csv'}`,
  exportHistory: () => api.get('/timesheets/export-history'),

  // Pay config
  payConfig: () => api.get('/timesheets/pay-config'),
  updatePayConfig: (userId, data) => api.put(`/timesheets/pay-config/${userId}`, data),
};

// ─── Features (docs, custom fields, streaks, leads, sequences, families, freezes, gift cards, webhooks) ──
export const features = {
  // Student documents & photos
  updateStudentPhoto: (studentId, photoUrl) => api.put(`/features/students/${studentId}/photo`, { photoUrl }),
  updateMyPhoto: (photoUrl) => api.put('/features/my-photo', { photoUrl }),
  studentDocuments: (studentId) => api.get(`/features/students/${studentId}/documents`),
  uploadDocument: (studentId, data) => api.post(`/features/students/${studentId}/documents`, data),
  deleteDocument: (id) => api.delete(`/features/documents/${id}`),

  // Custom fields
  customFields: () => api.get('/features/custom-fields'),
  createCustomField: (data) => api.post('/features/custom-fields', data),
  updateCustomField: (id, data) => api.put(`/features/custom-fields/${id}`, data),
  studentCustomFields: (studentId) => api.get(`/features/students/${studentId}/custom-fields`),
  saveStudentCustomFields: (studentId, fields) => api.put(`/features/students/${studentId}/custom-fields`, { fields }),

  // Attendance streaks
  streaks: () => api.get('/features/streaks'),
  studentStreak: (studentId) => api.get(`/features/streaks/${studentId}`),

  // Lead management
  leads: (params) => api.get('/features/leads', { params }),
  createLead: (data) => api.post('/features/leads', data),
  updateLead: (id, data) => api.put(`/features/leads/${id}`, data),
  leadActivities: (id) => api.get(`/features/leads/${id}/activities`),
  addLeadActivity: (id, data) => api.post(`/features/leads/${id}/activities`, data),

  // Email sequences
  sequences: () => api.get('/features/sequences'),
  getSequence: (id) => api.get(`/features/sequences/${id}`),
  createSequence: (data) => api.post('/features/sequences', data),
  updateSequence: (id, data) => api.put(`/features/sequences/${id}`, data),
  deleteSequence: (id) => api.delete(`/features/sequences/${id}`),
  addSequenceStep: (id, data) => api.post(`/features/sequences/${id}/steps`, data),
  updateSequenceStep: (stepId, data) => api.put(`/features/sequences/steps/${stepId}`, data),
  deleteSequenceStep: (stepId) => api.delete(`/features/sequences/steps/${stepId}`),

  // Families
  families: () => api.get('/features/families'),
  createFamily: (data) => api.post('/features/families', data),
  updateFamily: (id, data) => api.put(`/features/families/${id}`, data),
  deleteFamily: (id) => api.delete(`/features/families/${id}`),
  addFamilyMember: (id, data) => api.post(`/features/families/${id}/members`, data),
  removeFamilyMember: (memberId) => api.delete(`/features/families/members/${memberId}`),

  // Membership freeze
  freezes: () => api.get('/features/freezes'),
  createFreeze: (data) => api.post('/features/freezes', data),
  cancelFreeze: (id) => api.delete(`/features/freezes/${id}`),

  // Gift cards
  giftCards: () => api.get('/features/gift-cards'),
  createGiftCard: (data) => api.post('/features/gift-cards', data),
  redeemGiftCard: (code, amount) => api.post('/features/gift-cards/redeem', { code, amount }),

  // Webhooks
  webhooks: () => api.get('/features/webhooks'),
  createWebhook: (data) => api.post('/features/webhooks', data),
  deleteWebhook: (id) => api.delete(`/features/webhooks/${id}`),
  reactivateWebhook: (id) => api.post(`/features/webhooks/${id}/reactivate`),
  webhookLogs: (id) => api.get(`/features/webhooks/${id}/logs`),
};

export const messaging = {
  mode: () => api.get('/messaging/mode'),
  updateMode: (data) => api.put('/messaging/mode', data),
  usage: (params) => api.get('/messaging/usage', { params }),
  sendDirect: (data) => api.post('/messaging/send-direct', data),
};

export const licenseCheckout = {
  plans: () => api.get('/license-checkout/plans'),
  createSession: (priceId, addonModule) => api.post('/license-checkout/create-session', { priceId, addonModule }),
};

export const feedback = {
  submit: (data) => api.post('/feedback', data),
  mine: () => api.get('/feedback/mine'),
  all: () => api.get('/feedback'),
  update: (id, data) => api.put(`/feedback/${id}`, data),
};

// ─── Class credit packages ──────────────────────────────────────────────
export const classPackages = {
  list: () => api.get('/class-packages'),
  create: (data) => api.post('/class-packages', data),
  update: (id, data) => api.put(`/class-packages/${id}`, data),
  delete: (id) => api.delete(`/class-packages/${id}`),
  balance: (studentId) => api.get(`/class-packages/balance/${studentId}`),
  ledger: (studentId) => api.get(`/class-packages/balance/${studentId}/ledger`),
  grant: (data) => api.post('/class-packages/grant', data),
  checkout: (packageId) => api.post(`/class-packages/${packageId}/checkout`),
};

// ─── Door access providers ──────────────────────────────────────────────
export const access = {
  vendors: () => api.get('/access/vendors'),
  providers: () => api.get('/access/providers'),
  createProvider: (data) => api.post('/access/providers', data),
  updateProvider: (id, data) => api.put(`/access/providers/${id}`, data),
  deleteProvider: (id) => api.delete(`/access/providers/${id}`),
  testProvider: (id) => api.post(`/access/providers/${id}/test`),
  provisionStudent: (providerId, studentId) => api.post(`/access/providers/${providerId}/provision/${studentId}`),
  revokeStudent: (providerId, studentId) => api.post(`/access/providers/${providerId}/revoke/${studentId}`),
  syncProvider: (providerId) => api.post(`/access/providers/${providerId}/sync`),
  events: (params) => api.get('/access/events', { params }),
};

// ─── Marketing sequence enrollments + unsubscribes ─────────────────────
// (features.* already has sequences CRUD — add the runtime-specific calls)
features.enrollInSequence = (sequenceId, data) => api.post(`/features/sequences/${sequenceId}/enroll`, data);
features.cancelEnrollment = (enrollmentId) => api.post(`/features/sequences/enrollments/${enrollmentId}/cancel`);
features.enrollmentSends = (enrollmentId) => api.get(`/features/sequences/enrollments/${enrollmentId}/sends`);
features.unsubscribes = () => api.get('/features/unsubscribes');
features.addUnsubscribe = (data) => api.post('/features/unsubscribes', data);
features.removeUnsubscribe = (id) => api.delete(`/features/unsubscribes/${id}`);

export default api;
